import { Box, Dialog, DialogTitle, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { EditModeProvider } from 'contexts/edit-mode.context'
import ActionButtons from '../../../components/action-buttons/action-buttons.component'
import { FormProvider } from 'react-hook-form'
import { useAddEquipmentForm } from '../../../components/forms/add-equipment-form/add-equipment-form.hooks'
import AddEquipmentForm from '../../../components/forms/add-equipment-form/add-equipment-form.component'
import {
  useCreateEquipmentMutation,
  useListEquipmentsQuery,
  useUpdateEquipmentMutation,
} from '../../../store/equipments/equipments.api'
import {
  EquipmentPayload,
  EquipmentResponse,
} from '../../../store/equipments/equipments.types'
import { setSelectedEquipment } from '../../../store/equipments/equipments.slice'

type Props = {
  open: boolean
  handleClose: () => void
  equipmentType: 'TRACTOR' | 'TRAILER'
  equipment?: EquipmentResponse
}

const emptyFormValues = (equipmentType: 'TRACTOR' | 'TRAILER') => ({
  unitNumber: '',
  equipmentType,
  equipmentTypeCode: '',
  licensePlateNumber: '',
  stateProvince: '',
  vinNumber: '',
})

const formValuesFromEquipment = (equipment: EquipmentResponse) => ({
  unitNumber: equipment.unitNumber || '',
  equipmentType: equipment.equipmentType,
  equipmentTypeCode: equipment.equipmentTypeCode || '',
  licensePlateNumber: equipment.licensePlateNumber || '',
  stateProvince: equipment.stateProvince || '',
  vinNumber: equipment.vinNumber || '',
})

const toPayload = (data: EquipmentPayload): EquipmentPayload => ({
  unitNumber: data.unitNumber,
  equipmentType: data.equipmentType,
  equipmentTypeCode: data.equipmentTypeCode || undefined,
  licensePlateNumber: data.licensePlateNumber || undefined,
  stateProvince: data.stateProvince || undefined,
  vinNumber: data.vinNumber || undefined,
})

const EquipmentAddDialog = ({
  open,
  handleClose,
  equipmentType,
  equipment,
}: Props) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { form: formMethods } = useAddEquipmentForm()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isEdit = Boolean(equipment)

  const [addEquipment] = useCreateEquipmentMutation()
  const [updateEquipment] = useUpdateEquipmentMutation()
  const { refetch } = useListEquipmentsQuery()

  useEffect(() => {
    if (open) {
      formMethods.reset(
        equipment
          ? formValuesFromEquipment(equipment)
          : emptyFormValues(equipmentType)
      )
    }
  }, [open, equipment, equipmentType, formMethods])

  const handleCancel = () => {
    handleClose()
    formMethods.reset(emptyFormValues(equipmentType))
  }

  const handleSave = async (data: EquipmentPayload) => {
    const payload = toPayload(data)
    const saved = equipment
      ? await updateEquipment({
          equipmentId: equipment.equipmentId,
          ...payload,
        }).unwrap()
      : await addEquipment(payload).unwrap()

    refetch()
    if (equipment) {
      dispatch(setSelectedEquipment({ equipment: saved }))
    }
    formMethods.reset(emptyFormValues(equipmentType))
    handleClose()
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      <Box>
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(handleSave)}>
            <DialogTitle sx={{ fontWeight: 700, pb: 0, px: 4, pt: 3 }}>
              {isEdit ? 'Update Equipment' : 'Add New Equipment'}
            </DialogTitle>
            <Box p={4} pt={2}>
              <EditModeProvider initialEditMode suppressAutofill>
                <AddEquipmentForm
                  key={
                    equipment
                      ? `edit-${equipment.equipmentId}`
                      : `create-${equipmentType}`
                  }
                />
              </EditModeProvider>
            </Box>
            <ActionButtons
              firstBtnProps={{
                title: 'Save',
                onClick: formMethods.handleSubmit(handleSave),
              }}
              secondBtnProps={{
                title: 'Cancel',
                onClick: handleCancel,
                bgcolor: 'secondary.main',
              }}
            />
          </form>
        </FormProvider>
      </Box>
    </Dialog>
  )
}

export default EquipmentAddDialog
